window.isAnimatedLogoVisible = false;

requestAnimationFrame(() => {
  themeBase.onDOMContentLoadedTrigger(() => {
    class AnimatedLogo extends HTMLElement {
      constructor() {
        super();

        this.lastScrollTop = -1;
        this.duration = Number(this.dataset.delay) || 0;
        this.placeholderOpacityOffsetCoef = 1.15;

        this.initialized = false;
        this.fullLoaded = false;
        this.wasInvisible = false;
      }

      connectedCallback() {
        this.init();
      }

      init() {
        const stickyHeader =
          document.getElementsByTagName('sticky-header')[0];

        if (stickyHeader) {
          stickyHeader.animatedLogo = this;
        }

        this.picture =
          this.querySelector('#animated-logo-picture') ||
          document.getElementById('animated-logo-picture');

        this.absolutePicture =
          this.querySelector('#animated-logo-absolute-picture') ||
          document.getElementById(
            'animated-logo-absolute-picture'
          );

        this.absolutePictureTransparent =
          this.querySelector(
            '#animated-logo-absolute-picture-transparent'
          ) ||
          document.getElementById(
            'animated-logo-absolute-picture-transparent'
          );

        this.headingLogo =
          document.getElementById('heading-logo');

        this.animatedLogoPlaceholder =
          document.getElementById(
            'animated-logo-placeholder'
          );

        /*
         * Stop safely if any required element
         * does not exist.
         */
        if (
          !this.picture ||
          !this.absolutePicture ||
          !this.absolutePictureTransparent ||
          !this.headingLogo
        ) {
          return;
        }

        this.headerSectionsWrapper =
          this.headingLogo.closest(
            '.header-sections-wrapper'
          );

        if (!this.headerSectionsWrapper) {
          return;
        }

        this.isCentered =
          this.classList.contains(
            'animated-logo--centered'
          );

        this.attachedPoint =
          this.dataset.attachedPoint || 'bottom';

        this.onScrollHandler =
          this.onScroll.bind(this);

        this.onResizeHandler =
          this.onResize.bind(this);

        themeBase.checkWindow();

        this.onScrollHandler(null, true);

        themeBase.onWindowLoadTrigger(() => {
          this.onScrollHandler(null, true);
        });

        setTimeout(() => {
          this.onScrollHandler(null, true);
        }, 100);

        setTimeout(() => {
          this.onScrollHandler(null, true);
        }, 500);

        window.addEventListener(
          'throttleScroll',
          this.onScrollHandler
        );

        window.addEventListener(
          'MutationObserver',
          this.onScrollHandler
        );

        window.addEventListener(
          'throttle60fpsResize',
          this.onResizeHandler,
          false
        );

        setTimeout(() => {
          this.fullLoaded = true;
        }, 1000);
      }

      disconnectedCallback() {
        window.removeEventListener(
          'throttleScroll',
          this.onScrollHandler
        );

        window.removeEventListener(
          'MutationObserver',
          this.onScrollHandler
        );

        window.removeEventListener(
          'throttle60fpsResize',
          this.onResizeHandler
        );
      }

      getAttachedPointFactor() {
        if (this.attachedPoint === 'top') {
          return 0;
        }

        if (this.attachedPoint === 'center') {
          return 0.5;
        }

        return 1;
      }

      getBounds(scrollTop) {
        this.pictureBounds =
          themeBounds.get(this.picture);

        this.headingLogoBounds =
          themeBounds.get(this.headingLogo);

        this.headerSectionsWrapperBounds =
          themeBounds.get(
            this.headerSectionsWrapper
          );

        /*
         * Exact document position.
         *
         * This includes CSS margin changes and prevents
         * the animation from jumping when the logo
         * wrapper is moved upward.
         */
        this.pictureDocumentTop =
          this.pictureBounds.top + scrollTop;
      }

      onResize() {
        if (themeBase.isTouch) {
          this.classList.add(
            'animated-logo--static-mode'
          );

          return;
        }

        this.onScrollHandler(null, true);
      }

      onScroll(event, firstLoad = false) {
        const scrollTop =
          window.pageYOffset ||
          document.documentElement.scrollTop ||
          0;

        if (themeBase.isFreezeScrollEnable) {
          return;
        }

        if (
          this.lastScrollTop === scrollTop &&
          this.fullLoaded &&
          !firstLoad
        ) {
          return;
        }

        this.getBounds(scrollTop);

        if (
          !this.pictureBounds.width ||
          !this.pictureBounds.height
        ) {
          return;
        }

        const attachedPointFactor =
          this.getAttachedPointFactor();

        /*
         * Source attachment point in document coordinates.
         */
        const pictureAttachedDocumentY =
          this.pictureDocumentTop +
          this.pictureBounds.height *
            attachedPointFactor;

        /*
         * Final attachment point inside the sticky header.
         */
        const headingAttachedViewportY =
          this.headingLogoBounds.top +
          this.headingLogoBounds.height *
            attachedPointFactor;

        /*
         * Total distance required before the logo reaches
         * the sticky header logo.
         */
        const reductionRange = Math.max(
          pictureAttachedDocumentY -
            headingAttachedViewportY,
          1
        );

        const remainingRange =
          reductionRange - scrollTop;

        const ratioRangeOrigin = Math.max(
          0,
          Math.min(
            1,
            remainingRange / reductionRange
          )
        );

        const percentReductionRangeOrigin =
          ratioRangeOrigin * 100;

        const percentReductionRange =
          100 - percentReductionRangeOrigin;

        const targetLogoWidth =
          this.headingLogoBounds.width > 0
            ? this.headingLogoBounds.width
            : this.pictureBounds.width;

        const pictureWidthRange =
          this.pictureBounds.width -
          targetLogoWidth;

        const pictureWidthNewValue =
          this.pictureBounds.width -
          (pictureWidthRange / 100) *
            Math.min(
              percentReductionRange,
              100
            );

        const pictureWidthNewScale =
          pictureWidthNewValue /
          this.pictureBounds.width;

        const safePictureScale = Math.max(
          0.01,
          pictureWidthNewScale
        );

        const pictureDuration =
          firstLoad === true ||
          this.duration === 0
            ? 0
            : Math.max(
                this.duration -
                  (this.duration / 100) *
                    percentReductionRange,
                0
              );

        const animatedLogoPlaceholderOpacity =
          Math.max(
            1 -
              (percentReductionRange / 100) *
                this.placeholderOpacityOffsetCoef,
            0
          );

        /*
         * Interpolate the attachment point between:
         *
         * 1. Original large logo position
         * 2. Sticky-header logo position
         */
        const animatedAttachedPointY =
          headingAttachedViewportY +
          (
            pictureAttachedDocumentY -
            headingAttachedViewportY
          ) *
            ratioRangeOrigin;

        /*
         * Account for the scaled height so the selected
         * top, center or bottom point stays attached.
         */
        const scaledPictureHeight =
          this.pictureBounds.height *
          safePictureScale;

        const absolutePictureTranslateY =
          animatedAttachedPointY -
          scaledPictureHeight *
            attachedPointFactor;

        this.style.setProperty(
          '--absolute-picture-translateY',
          `${absolutePictureTranslateY}px`
        );

        this.style.setProperty(
          '--absolute-picture-scale',
          `scale(${safePictureScale.toFixed(4)})`
        );

        this.style.setProperty(
          '--absolute-picture-transition-duration',
          `${pictureDuration}ms`
        );

        if (!this.isCentered) {
          this.style.setProperty(
            '--absolute-picture-left',
            `${50 * ratioRangeOrigin}%`
          );
        } else {
          this.style.setProperty(
            '--absolute-picture-left',
            '50%'
          );
        }

        if (
          this.animatedLogoPlaceholder &&
          this.animatedLogoPlaceholderOpacity !==
            animatedLogoPlaceholderOpacity
        ) {
          this.animatedLogoPlaceholder.style.opacity =
            animatedLogoPlaceholderOpacity;
        }

        const isInvisible =
          percentReductionRange >= 100;

        this.absolutePicture.classList[
          isInvisible ? 'add' : 'remove'
        ]('is-invisible');

        this.absolutePictureTransparent.classList[
          isInvisible ? 'add' : 'remove'
        ]('is-invisible');

        this.headingLogo.classList[
          isInvisible ? 'remove' : 'add'
        ]('is-invisible');

        const absolutePictureBounds =
          themeBounds.get(
            this.absolutePicture
          );

        const crossesNavigation =
          absolutePictureBounds.top <
          this.headerSectionsWrapperBounds.bottom;

        this.classList[
          crossesNavigation ? 'add' : 'remove'
        ]('animated-logo--crosses-navigation');

        this.animatedLogoPlaceholderOpacity =
          animatedLogoPlaceholderOpacity;

        this.currentRatioRangeOrigin =
          ratioRangeOrigin;

        this.currentPictureBoundsBottom =
          this.pictureBounds.bottom;

        if (!this.wasInvisible) {
          window.isAnimatedLogoVisible =
            this.pictureBounds.top > 0;

          if (!window.isAnimatedLogoVisible) {
            this.wasInvisible = true;
          }
        }

        if (
          !this.initialized &&
          (
            this.lastScrollTop > 0 ||
            absolutePictureTranslateY > 0
          ) &&
          this.pictureBounds.height
        ) {
          this.classList.add(
            'animated-logo--initialized'
          );

          this.initialized = true;
        }

        this.lastScrollTop = scrollTop;
      }
    }

    if (
      !customElements.get('animated-logo')
    ) {
      customElements.define(
        'animated-logo',
        AnimatedLogo
      );
    }
  });
});